/**
 * Mocked squadron roster for the explore pages.
 *
 * 20 members with realistic tactical-callsign-style names, a mix of:
 *   - Roles (1 owner, 3 admins, 16 members)
 *   - Verification states (8 military-verified, 12 standard)
 *   - Live-now states (3 currently streaming/active)
 *   - Branches across British armed forces for the verified slice
 *
 * Dicebear avatars (deterministic by seed) so no real faces, no GDPR
 * surface, predictable load time.
 */

type MockMember = {
  userId: string
  callsign: string
  displayName: string
  avatarUrl: string
  role: 'OWNER' | 'ADMIN' | 'MEMBER'
  verified: boolean
  branch: string | null
  station: string | null
  lastActiveAt: string
  isLive: boolean
  liveGame: string | null
  joinedAt: string
  ageVerified: boolean
}

function dicebear(seed: string): string {
  // Initials variant — deterministic, fast, no faces. The seed string
  // becomes the avatar identity so every render produces the same image.
  return `https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(seed)}&backgroundColor=11112A&textColor=3D6FFF`
}

const NOW_ISO = '2026-06-16T20:00:00Z'
const HOURS_AGO = (h: number): string =>
  new Date(Date.parse(NOW_ISO) - h * 3_600_000).toISOString()
const DAYS_AGO = (d: number): string =>
  new Date(Date.parse(NOW_ISO) - d * 86_400_000).toISOString()

export const MOCK_MEMBERS: MockMember[] = [
  // ── Owner ────────────────────────────────────────────────────────
  {
    userId: 'u-halcyon',
    callsign: '@halcyon',
    displayName: 'CMDR Halcyon',
    avatarUrl: dicebear('Halcyon'),
    role: 'OWNER',
    verified: true,
    branch: 'British Army',
    station: 'Catterick Garrison',
    lastActiveAt: HOURS_AGO(0.2),
    isLive: false,
    liveGame: null,
    joinedAt: DAYS_AGO(310),
    ageVerified: true,
  },

  // ── Admins ───────────────────────────────────────────────────────
  {
    userId: 'u-orion',
    callsign: '@orion',
    displayName: 'SGT Orion',
    avatarUrl: dicebear('Orion'),
    role: 'ADMIN',
    verified: true,
    branch: 'Royal Marines',
    station: 'Plymouth',
    lastActiveAt: HOURS_AGO(0.5),
    isLive: true,
    liveGame: 'Counter-Strike 2',
    joinedAt: DAYS_AGO(295),
    ageVerified: true,
  },
  {
    userId: 'u-vega',
    callsign: '@vega',
    displayName: 'CPL Vega',
    avatarUrl: dicebear('Vega'),
    role: 'ADMIN',
    verified: true,
    branch: 'Royal Air Force',
    station: 'RAF Brize Norton',
    lastActiveAt: HOURS_AGO(1.1),
    isLive: false,
    liveGame: null,
    joinedAt: DAYS_AGO(278),
    ageVerified: true,
  },
  {
    userId: 'u-rook',
    callsign: '@rook',
    displayName: 'CPL Rook',
    avatarUrl: dicebear('Rook'),
    role: 'ADMIN',
    verified: false,
    branch: null,
    station: null,
    lastActiveAt: HOURS_AGO(3),
    isLive: false,
    liveGame: null,
    joinedAt: DAYS_AGO(220),
    ageVerified: true,
  },

  // ── Members ──────────────────────────────────────────────────────
  {
    userId: 'u-stormbreaker',
    callsign: '@stormbreaker',
    displayName: 'Stormbreaker',
    avatarUrl: dicebear('Stormbreaker'),
    role: 'MEMBER',
    verified: true,
    branch: 'Royal Navy',
    station: 'HMS Sultan',
    lastActiveAt: HOURS_AGO(0.1),
    isLive: true,
    liveGame: 'Valorant',
    joinedAt: DAYS_AGO(180),
    ageVerified: true,
  },
  {
    userId: 'u-paladin',
    callsign: '@paladin',
    displayName: 'Paladin',
    avatarUrl: dicebear('Paladin'),
    role: 'MEMBER',
    verified: true,
    branch: 'British Army',
    station: 'Aldershot',
    lastActiveAt: HOURS_AGO(0.4),
    isLive: false,
    liveGame: null,
    joinedAt: DAYS_AGO(165),
    ageVerified: true,
  },
  {
    userId: 'u-tempest',
    callsign: '@tempest',
    displayName: 'Tempest',
    avatarUrl: dicebear('Tempest'),
    role: 'MEMBER',
    verified: true,
    branch: 'Royal Air Force',
    station: 'RAF Marham',
    lastActiveAt: HOURS_AGO(0.6),
    isLive: true,
    liveGame: 'Apex Legends',
    joinedAt: DAYS_AGO(154),
    ageVerified: true,
  },
  {
    userId: 'u-saber',
    callsign: '@saber',
    displayName: 'Saber',
    avatarUrl: dicebear('Saber'),
    role: 'MEMBER',
    verified: false,
    branch: null,
    station: null,
    lastActiveAt: HOURS_AGO(2.3),
    isLive: false,
    liveGame: null,
    joinedAt: DAYS_AGO(140),
    ageVerified: true,
  },
  {
    userId: 'u-pyre',
    callsign: '@pyre',
    displayName: 'Pyre',
    avatarUrl: dicebear('Pyre'),
    role: 'MEMBER',
    verified: false,
    branch: null,
    station: null,
    lastActiveAt: HOURS_AGO(4),
    isLive: false,
    liveGame: null,
    joinedAt: DAYS_AGO(135),
    ageVerified: true,
  },
  {
    userId: 'u-cypher',
    callsign: '@cypher',
    displayName: 'Cypher',
    avatarUrl: dicebear('Cypher'),
    role: 'MEMBER',
    verified: true,
    branch: 'British Army',
    station: 'Tidworth',
    lastActiveAt: HOURS_AGO(6),
    isLive: false,
    liveGame: null,
    joinedAt: DAYS_AGO(128),
    ageVerified: true,
  },
  {
    userId: 'u-mira',
    callsign: '@mira',
    displayName: 'Mira',
    avatarUrl: dicebear('Mira'),
    role: 'MEMBER',
    verified: false,
    branch: null,
    station: null,
    lastActiveAt: HOURS_AGO(12),
    isLive: false,
    liveGame: null,
    joinedAt: DAYS_AGO(110),
    ageVerified: true,
  },
  {
    userId: 'u-nova',
    callsign: '@nova',
    displayName: 'Nova',
    avatarUrl: dicebear('Nova'),
    role: 'MEMBER',
    verified: false,
    branch: null,
    station: null,
    lastActiveAt: HOURS_AGO(20),
    isLive: false,
    liveGame: null,
    joinedAt: DAYS_AGO(98),
    ageVerified: true,
  },
  {
    userId: 'u-ronin',
    callsign: '@ronin',
    displayName: 'Ronin',
    avatarUrl: dicebear('Ronin'),
    role: 'MEMBER',
    verified: false,
    branch: null,
    station: null,
    lastActiveAt: DAYS_AGO(1),
    isLive: false,
    liveGame: null,
    joinedAt: DAYS_AGO(95),
    ageVerified: true,
  },
  {
    userId: 'u-quill',
    callsign: '@quill',
    displayName: 'Quill',
    avatarUrl: dicebear('Quill'),
    role: 'MEMBER',
    verified: true,
    branch: 'Royal Air Force',
    station: 'RAF Cosford',
    lastActiveAt: DAYS_AGO(1.5),
    isLive: false,
    liveGame: null,
    joinedAt: DAYS_AGO(82),
    ageVerified: true,
  },
  {
    userId: 'u-trace',
    callsign: '@trace',
    displayName: 'Trace',
    avatarUrl: dicebear('Trace'),
    role: 'MEMBER',
    verified: false,
    branch: null,
    station: null,
    lastActiveAt: DAYS_AGO(2),
    isLive: false,
    liveGame: null,
    joinedAt: DAYS_AGO(72),
    ageVerified: true,
  },
  {
    userId: 'u-hawk',
    callsign: '@hawk',
    displayName: 'Hawk',
    avatarUrl: dicebear('Hawk'),
    role: 'MEMBER',
    verified: false,
    branch: null,
    station: null,
    lastActiveAt: DAYS_AGO(3),
    isLive: false,
    liveGame: null,
    joinedAt: DAYS_AGO(54),
    ageVerified: true,
  },
  {
    userId: 'u-sable',
    callsign: '@sable',
    displayName: 'Sable',
    avatarUrl: dicebear('Sable'),
    role: 'MEMBER',
    verified: false,
    branch: null,
    station: null,
    lastActiveAt: DAYS_AGO(5),
    isLive: false,
    liveGame: null,
    joinedAt: DAYS_AGO(43),
    ageVerified: true,
  },
  {
    userId: 'u-fennec',
    callsign: '@fennec',
    displayName: 'Fennec',
    avatarUrl: dicebear('Fennec'),
    role: 'MEMBER',
    verified: false,
    branch: null,
    station: null,
    lastActiveAt: DAYS_AGO(7),
    isLive: false,
    liveGame: null,
    joinedAt: DAYS_AGO(28),
    ageVerified: true,
  },
  {
    userId: 'u-ember',
    callsign: '@ember',
    displayName: 'Ember',
    avatarUrl: dicebear('Ember'),
    role: 'MEMBER',
    verified: false,
    branch: null,
    station: null,
    lastActiveAt: DAYS_AGO(9),
    isLive: false,
    liveGame: null,
    joinedAt: DAYS_AGO(18),
    ageVerified: true,
  },
  {
    userId: 'u-juno',
    callsign: '@juno',
    displayName: 'Juno',
    avatarUrl: dicebear('Juno'),
    role: 'MEMBER',
    verified: true,
    branch: 'Royal Marines',
    station: 'Lympstone',
    lastActiveAt: DAYS_AGO(11),
    isLive: false,
    liveGame: null,
    joinedAt: DAYS_AGO(9),
    ageVerified: true,
  },
]

export type { MockMember }
